/*
 * klaviyo-signup.js — theme-native Klaviyo signup (footer newsletter + free guides).
 *
 * Pairs with snippets/klaviyo-signup-form.liquid. Reads its config from the
 * data-kl-* attributes on the [data-kl-signup] root and, on submit:
 *
 *   1. validates email (+ the "Tell us a bit about yourself" dropdown when required)
 *   2. POST /client/subscriptions/  -> joins the list, sets role_or_stage + Source
 *   3. POST /client/events/         -> optional metric (e.g. "Requested Free Guide")
 *      carrying guide_title / download_url so ONE flow can deliver any guide
 *   4. klaviyo.identify()           -> stitches the browser cookie to the profile
 *   5. swaps the form for the success message and fires
 *      'nitm:klaviyo-signup:success' (bubbles) for the section to react to
 *
 * It also drives the custom dropdown (button[role=combobox] + ul[role=listbox]):
 * click/keyboard open, arrow navigation, type-ahead, Esc / click-outside to
 * close, flips upward when there's no room below, hidden input carries the value.
 *
 * Both API calls use Klaviyo's PUBLIC client API: company_id (public key) only,
 * no private keys, CORS-open, 202 = accepted/queued.
 *
 * Standalone file on purpose — assets/app.js is a committed webpack bundle
 * and is not rebuilt for theme tweaks. Loaded with `defer` by the snippet
 * (twice on pages with both forms; the guard below makes that a no-op).
 *
 * Preview without touching Klaviyo: ?kl_stub=pass or ?kl_stub=fail.
 */
(function () {
  'use strict';

  if (window.NITMKlaviyoSignup) return;

  var API = 'https://a.klaviyo.com/client/';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var LOG = '[klaviyo-signup]';

  /* ------------------------------------------------------------ config */

  function stubMode() {
    try {
      var m = /[?&]kl_stub=(pass|fail)\b/.exec(window.location.search);
      return m ? m[1] : null;
    } catch (e) {
      return null;
    }
  }

  function readConfig(root) {
    var d = root.dataset;
    var props = {};
    try {
      props = JSON.parse(d.klEventProps || '{}') || {};
    } catch (e) {
      props = {};
    }
    return {
      companyId: (d.klCompanyId || '').trim(),
      revision: (d.klRevision || '2026-07-15').trim(),
      listId: (d.klListId || '').trim(),
      source: (d.klSource || '').trim(),
      propertyKey: (d.klPropertyKey || 'role_or_stage').trim(),
      eventName: (d.klEventName || '').trim(),
      eventProps: props,
      requireAbout: d.klRequireAbout === 'true',
      busyLabel: d.klBusyLabel || 'Sending…',
      activeLabel: (d.klButtonActive || '').trim(),
      errors: {
        generic: d.klErrorGeneric || 'Something went wrong. Please try again in a moment.',
        email: d.klErrorEmail || 'Please enter a valid email address.',
        required: d.klErrorRequired || 'This field is required'
      }
    };
  }

  /* --------------------------------------------------------- transport */

  function post(path, cfg, body) {
    return fetch(API + path + '?company_id=' + encodeURIComponent(cfg.companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'revision': cfg.revision
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (res.ok) return res;
      return res.text().then(function (text) {
        var detail = '';
        try {
          var json = JSON.parse(text);
          detail = (json && json.errors && json.errors[0] && json.errors[0].detail) || '';
        } catch (e) { /* not JSON */ }
        var err = new Error(detail || ('Klaviyo ' + path + ' responded ' + res.status));
        err.status = res.status;
        err.detail = detail;
        throw err;
      });
    });
  }

  function profileProperties(cfg, about) {
    var props = {};
    if (about) props[cfg.propertyKey] = about;
    if (cfg.source) props.Source = cfg.source; // mirrors the hidden field on the old Klaviyo forms
    return props;
  }

  function subscribe(cfg, email, about) {
    var attributes = {
      profile: {
        data: {
          type: 'profile',
          attributes: {
            email: email,
            properties: profileProperties(cfg, about),
            subscriptions: {
              email: { marketing: { consent: 'SUBSCRIBED' } }
            }
          }
        }
      }
    };
    if (cfg.source) attributes.custom_source = cfg.source;

    return post('subscriptions/', cfg, {
      data: {
        type: 'subscription',
        attributes: attributes,
        relationships: {
          list: { data: { type: 'list', id: cfg.listId } }
        }
      }
    });
  }

  function trackEvent(cfg, email, about) {
    if (!cfg.eventName) return Promise.resolve();

    var props = {};
    Object.keys(cfg.eventProps).forEach(function (key) {
      if (cfg.eventProps[key] !== null && cfg.eventProps[key] !== '') props[key] = cfg.eventProps[key];
    });
    if (about) props[cfg.propertyKey] = about;

    // One event per person, per guide, per day: a double-click or a re-submit
    // does not restart the delivery flow.
    var day = new Date().toISOString().slice(0, 10);
    var uniqueId = email + '|' + (cfg.eventProps.guide_handle || 'signup') + '|' + day;

    return post('events/', cfg, {
      data: {
        type: 'event',
        attributes: {
          properties: props,
          unique_id: uniqueId,
          metric: { data: { type: 'metric', attributes: { name: cfg.eventName } } },
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email, properties: profileProperties(cfg, about) }
            }
          }
        }
      }
    }).catch(function (err) {
      // The subscription already went through; a lost event must not block the UI.
      console.warn(LOG, 'event "' + cfg.eventName + '" not recorded:', err.message);
    });
  }

  function identify(cfg, email, about) {
    try {
      var props = profileProperties(cfg, about);
      props.$email = email;
      (window.klaviyo = window.klaviyo || []).push(['identify', props]);
    } catch (e) { /* onsite script not present — nothing to stitch */ }
    return Promise.resolve();
  }

  /* ---------------------------------------------------------- dropdown */

  // Custom select: <button role="combobox"> + <ul role="listbox"> + hidden input.
  // Keeps the look of the previous Klaviyo dropdown, adds a real scrollbar,
  // keyboard support and click-outside-to-close.
  function initDropdown(wrap) {
    var button = wrap.querySelector('[data-kl-dropdown-button]');
    var label = wrap.querySelector('[data-kl-dropdown-label]');
    var list = wrap.querySelector('[data-kl-dropdown-list]');
    var hidden = wrap.querySelector('[data-kl-about]');
    if (!button || !label || !list || !hidden) return null;

    var options = Array.prototype.slice.call(list.querySelectorAll('[role="option"]'));
    var placeholder = label.getAttribute('data-placeholder') || label.textContent;
    var open = false;
    var active = -1;

    function selectedIndex() {
      for (var i = 0; i < options.length; i++) {
        if (options[i].getAttribute('aria-selected') === 'true') return i;
      }
      return -1;
    }

    function setActive(index, scroll) {
      active = index;
      options.forEach(function (option, i) {
        option.classList.toggle('is-active', i === index);
      });
      if (index >= 0) {
        button.setAttribute('aria-activedescendant', options[index].id);
        if (scroll) {
          try { options[index].scrollIntoView({ block: 'nearest' }); } catch (e) { /* older browsers */ }
        }
      } else {
        button.removeAttribute('aria-activedescendant');
      }
    }

    // Flip upward when the list would run off the bottom of the viewport
    // (the footer case), like the old dropdown did.
    function place() {
      wrap.classList.remove('kls__dropdown--up');
      var rect = button.getBoundingClientRect();
      var needed = list.offsetHeight + 8;
      var below = window.innerHeight - rect.bottom;
      if (below < needed && rect.top > needed) wrap.classList.add('kls__dropdown--up');
    }

    function setOpen(next) {
      if (open === next) return;
      open = next;
      list.hidden = !open;
      wrap.classList.toggle('is-open', open);
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        place();
        var sel = selectedIndex();
        setActive(sel >= 0 ? sel : 0, true);
      } else {
        setActive(-1);
      }
    }

    function select(index) {
      var option = options[index];
      if (!option) return;
      options.forEach(function (o, i) {
        o.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      hidden.value = option.getAttribute('data-value') || option.textContent.trim();
      label.textContent = option.textContent.trim();
      wrap.classList.add('has-value');
      button.removeAttribute('aria-invalid');
      setOpen(false);
      button.focus();
      hidden.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function typeahead(key) {
      var k = key.toLowerCase();
      var start = active + 1;
      for (var n = 0; n < options.length; n++) {
        var i = (start + n) % options.length;
        if (options[i].textContent.trim().toLowerCase().charAt(0) === k) return i;
      }
      return -1;
    }

    button.addEventListener('click', function () {
      setOpen(!open);
    });

    button.addEventListener('keydown', function (event) {
      var key = event.key;
      if (key === 'ArrowDown') {
        event.preventDefault();
        if (!open) setOpen(true); else setActive(Math.min(active + 1, options.length - 1), true);
      } else if (key === 'ArrowUp') {
        event.preventDefault();
        if (!open) setOpen(true); else setActive(Math.max(active - 1, 0), true);
      } else if (key === 'Home' && open) {
        event.preventDefault();
        setActive(0, true);
      } else if (key === 'End' && open) {
        event.preventDefault();
        setActive(options.length - 1, true);
      } else if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        if (!open) setOpen(true); else select(active);
      } else if (key === 'Escape') {
        if (open) { event.preventDefault(); setOpen(false); }
      } else if (key === 'Tab') {
        if (open) setOpen(false);
      } else if (key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
        var hit = typeahead(key);
        if (hit >= 0) {
          if (open) setActive(hit, true); else select(hit);
        }
      }
    });

    options.forEach(function (option, i) {
      option.addEventListener('mousedown', function (event) { event.preventDefault(); }); // keep focus on the button
      option.addEventListener('click', function () { select(i); });
      option.addEventListener('mousemove', function () { if (active !== i) setActive(i, false); });
    });

    document.addEventListener('click', function (event) {
      if (open && !wrap.contains(event.target)) setOpen(false);
    });
    document.addEventListener('focusin', function (event) {
      if (open && !wrap.contains(event.target)) setOpen(false);
    });

    return {
      button: button,
      hidden: hidden,
      reset: function () {
        options.forEach(function (o) { o.setAttribute('aria-selected', 'false'); });
        hidden.value = '';
        label.textContent = placeholder;
        wrap.classList.remove('has-value');
        setOpen(false);
      }
    };
  }

  /* ---------------------------------------------------------------- ui */

  function setBusy(root, els, cfg, busy) {
    root.setAttribute('aria-busy', busy ? 'true' : 'false');
    els.button.disabled = !!busy;
    if (busy) {
      els.button.textContent = cfg.busyLabel;
    } else {
      els.refreshState();
    }
  }

  function hide(el) {
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  function clearErrors(els) {
    hide(els.error);
    hide(els.emailError);
    hide(els.aboutError);
    if (els.email) els.email.removeAttribute('aria-invalid');
    if (els.aboutField) els.aboutField.removeAttribute('aria-invalid');
  }

  // Field errors render directly under their field; anything else (Klaviyo
  // or network failure) goes in the form-level slot under the button.
  function showError(els, message, field) {
    var slot = els.error;
    if (field === els.email && els.emailError) slot = els.emailError;
    else if (field && field === els.aboutField && els.aboutError) slot = els.aboutError;

    slot.textContent = message;
    slot.hidden = false;
    if (field) {
      field.setAttribute('aria-invalid', 'true');
      field.focus();
    } else {
      slot.focus();
    }
  }

  function validate(cfg, els) {
    var email = (els.email.value || '').trim();
    if (!EMAIL_RE.test(email)) return { error: cfg.errors.email, field: els.email };

    var about = els.about ? (els.about.value || '') : '';
    if (els.about && cfg.requireAbout && !about) return { error: cfg.errors.required, field: els.aboutField };

    return { email: email, about: about };
  }

  function showSuccess(root, els, detail) {
    els.form.hidden = true;
    if (els.success) {
      els.success.hidden = false;
      try { els.success.focus({ preventScroll: true }); } catch (e) { els.success.focus(); }
    }
    root.dispatchEvent(new CustomEvent('nitm:klaviyo-signup:success', { bubbles: true, detail: detail }));
  }

  // Button state follows the email field: grey/idle until something is typed,
  // then the "ready" colours and (guide pages) "Download Your Free Guide" ->
  // "Download Now" — the behaviour the old bundle intended but never delivered.
  function bindEmailState(root, els, cfg) {
    var idle = els.button.textContent.trim();
    function refresh() {
      var typed = (els.email.value || '').trim().length > 0;
      root.classList.toggle('is-ready', typed);
      if (els.button.disabled) return;
      els.button.textContent = (typed && cfg.activeLabel) ? cfg.activeLabel : idle;
    }
    els.email.addEventListener('input', refresh);
    els.email.addEventListener('change', refresh);
    refresh();
    return refresh;
  }

  /* -------------------------------------------------------------- init */

  function init(root) {
    if (root.dataset.klReady) return;
    root.dataset.klReady = 'true';

    var cfg = readConfig(root);
    var dropdownWrap = root.querySelector('[data-kl-dropdown]');
    var dropdown = dropdownWrap ? initDropdown(dropdownWrap) : null;

    var els = {
      form: root.querySelector('form'),
      email: root.querySelector('[data-kl-email]'),
      about: dropdown ? dropdown.hidden : root.querySelector('[data-kl-about]'),
      aboutField: dropdown ? dropdown.button : root.querySelector('[data-kl-about]'),
      button: root.querySelector('[data-kl-submit]'),
      error: root.querySelector('[data-kl-error]'),
      emailError: root.querySelector('[data-kl-error-email]'),
      aboutError: root.querySelector('[data-kl-error-about]'),
      success: root.querySelector('[data-kl-success]')
    };
    if (!els.form || !els.email || !els.button || !els.error) return;

    var stub = stubMode();
    if (!cfg.companyId && !stub) console.warn(LOG, 'No Klaviyo public key set (Theme settings > Klaviyo signup) for #' + root.id);
    if (!cfg.listId && !stub) console.warn(LOG, 'No Klaviyo list ID set for #' + root.id + ' — submissions will fail until one is added (section setting or the product\'s klaviyo_list_id metafield).');

    els.refreshState = bindEmailState(root, els, cfg);

    // Errors clear as soon as the person fixes the field.
    els.email.addEventListener('input', function () {
      els.email.removeAttribute('aria-invalid');
      hide(els.emailError);
    });
    if (els.about) {
      els.about.addEventListener('change', function () {
        if (els.aboutField) els.aboutField.removeAttribute('aria-invalid');
        hide(els.aboutError);
      });
    }

    els.form.addEventListener('submit', function (event) {
      event.preventDefault();
      clearErrors(els);

      var v = validate(cfg, els);
      if (v.error) {
        showError(els, v.error, v.field);
        return;
      }

      if (!stub && (!cfg.listId || !cfg.companyId)) {
        showError(els, cfg.errors.generic);
        return;
      }

      setBusy(root, els, cfg, true);

      var run;
      if (stub === 'pass') {
        run = new Promise(function (resolve) { setTimeout(resolve, 600); });
      } else if (stub === 'fail') {
        run = new Promise(function (_, reject) {
          setTimeout(function () { reject(new Error('Stubbed failure (?kl_stub=fail)')); }, 600);
        });
      } else {
        run = subscribe(cfg, v.email, v.about).then(function () {
          return Promise.all([
            trackEvent(cfg, v.email, v.about),
            identify(cfg, v.email, v.about)
          ]);
        });
      }

      run.then(function () {
        setBusy(root, els, cfg, false);
        showSuccess(root, els, {
          email: v.email,
          about: v.about,
          listId: cfg.listId,
          source: cfg.source,
          stub: stub
        });
      }).catch(function (err) {
        setBusy(root, els, cfg, false);
        console.warn(LOG, err);
        // A 400 carries a human-readable reason (e.g. invalid email); anything
        // else (network, 5xx, rate limit) gets the editable generic message.
        var message = (err && err.status === 400 && err.detail) ? err.detail : cfg.errors.generic;
        showError(els, message);
      });
    });
  }

  function scan(context) {
    var scope = context && context.querySelectorAll ? context : document;
    if (scope !== document && scope.matches && scope.matches('[data-kl-signup]')) init(scope);
    var roots = scope.querySelectorAll('[data-kl-signup]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Theme editor re-renders a section's HTML without re-running its scripts.
  document.addEventListener('shopify:section:load', function (event) { scan(event.target); });

  window.NITMKlaviyoSignup = { init: init, scan: scan, subscribe: subscribe, trackEvent: trackEvent };
})();
